import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useHealth } from "@/context/HealthContext";
import { HCard } from "@/components/health-kit";
import {
  Activity, ClipboardCheck, AlertTriangle, BrainCircuit, UserCheck, ArrowRight,
  Database, Cpu, ShieldCheck,
} from "lucide-react";

export default function HealthOverview() {
  const { overview, ht, tick } = useHealth();
  const navigate = useNavigate();
  const [audit, setAudit] = useState([]);

  useEffect(() => { api.get("/health/audit").then(({ data }) => setAudit(data.slice(0, 8))).catch(() => {}); }, [tick]);

  const metrics = [
    { key: "active-cases", label: ht("activeCases"), value: overview?.active_cases ?? "—", icon: Activity, tone: "text-teal-400" },
    { key: "requiring-review", label: ht("casesRequiringReview"), value: overview?.cases_requiring_review ?? "—", icon: ClipboardCheck, tone: "text-amber-400" },
    { key: "potential-signals", label: ht("potentialSignals"), value: overview?.potential_signals ?? "—", icon: AlertTriangle, tone: "text-orange-400" },
    { key: "ai-analyses", label: ht("aiAnalyses"), value: overview?.ai_analyses ?? "—", icon: BrainCircuit, tone: "text-cyan-400" },
    { key: "human-reviews", label: ht("humanReviews"), value: overview?.human_reviews ?? "—", icon: UserCheck, tone: "text-emerald-400" },
  ];
  const flow = ["f_data", "f_core", "f_analysis", "f_signal", "f_review", "f_decision", "f_audit"];

  return (
    <div className="space-y-6 ngx-fade-in" data-testid="health-overview">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-50">{ht("nav_overview")}</h1>
          <p className="text-sm text-slate-400 mt-1">{ht("aiSupports")}</p>
        </div>
        <button onClick={() => navigate("/health/review")} data-testid="overview-goto-review"
          className="h-11 px-4 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm flex items-center gap-2">
          {ht("nav_review")} <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {metrics.map((m) => (
          <HCard key={m.label} className="p-5" data-testid={`metric-${m.key}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{m.label}</span>
              <m.icon className="h-4 w-4 text-slate-500" />
            </div>
            <div className={`font-mono text-3xl font-medium mt-3 ${m.tone}`} data-testid={`metric-value-${m.key}`}>{m.value}</div>
          </HCard>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <HCard className="lg:col-span-2 p-6 relative overflow-hidden">
          <div className="absolute inset-0 ngx-grid-overlay opacity-15" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 text-teal-400 mb-4">
              <Cpu className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-widest">{ht("mainFlow")}</span>
            </div>
            <div className="flex flex-col gap-2">
              {flow.map((k, i) => (
                <div key={k} className="flex items-center gap-3">
                  <span className={`px-3 py-2 rounded-sm border text-sm flex-1 ${
                    k === "f_review" || k === "f_decision"
                      ? "bg-teal-950/40 border-teal-700 text-teal-200 font-medium"
                      : "bg-[#050C11] border-slate-700 text-slate-200"}`}>
                    {ht(k)}
                  </span>
                  {i < flow.length - 1 && <span className="text-slate-600 text-xs w-4 text-center">↓</span>}
                </div>
              ))}
            </div>
          </div>
        </HCard>

        <HCard className="p-5">
          <div className="flex items-center gap-2 mb-3 text-slate-300">
            <Database className="h-4 w-4 text-teal-400" />
            <h3 className="font-display font-semibold text-sm">{ht("recentActivity")}</h3>
          </div>
          <div className="space-y-2 max-h-[360px] overflow-y-auto" data-testid="overview-recent">
            {audit.map((a) => (
              <div key={a.id} className="flex items-start gap-2 text-xs font-mono py-1.5 border-b border-slate-800/60 last:border-0">
                <span className="text-slate-500 shrink-0">{a.time}</span>
                <span className="text-slate-300">{ht("at_" + a.type)}</span>
                <span className="text-slate-500 ml-auto shrink-0">{a.patient_id}</span>
              </div>
            ))}
          </div>
        </HCard>
      </div>
    </div>
  );
}
